import { useEffect, useRef, useState } from 'react'
import { Skeleton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useBannerAndSliderHeights } from '../../utils/banner-and-slider-heights'

interface BannerImageProps {
  imageUrl?: string
  height?: number | string
  blur?: number
  opacity?: number
  children?: React.ReactNode
}

type BackgroundProps = {
  $imageUrl?: string
  $blur: number
  $opacity: number
  $loaded: boolean
}

const Container = styled('div')<{ $height: number | string }>(
  ({ $height }) => ({
    position: 'relative',
    width: '100%',
    height: $height,
    overflow: 'hidden',
  }),
)

const Background = styled('div')<BackgroundProps>(
  ({ $imageUrl, $blur, $opacity, $loaded }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: $loaded && $imageUrl ? `url(${$imageUrl})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: `blur(${$blur}px)`,
    opacity: $opacity,
    transform: 'scale(1.1)',
    transition: 'opacity 0.3s ease',
  }),
)

const BackgroundSkeleton = styled(Skeleton)({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  transform: 'none',
})

const Gradient = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(to bottom, transparent 0%, ${theme.palette.background.default} 100%)`,
}))

const Content = styled('div')({
  position: 'relative',
  height: '100%',
  display: 'flex',
  alignItems: 'flex-end',
  zIndex: 1,
})

export default function BannerImage({
  imageUrl,
  height,
  blur = 1,
  opacity = 0.4,
  children,
}: BannerImageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const target = containerRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )

    observer.observe(target)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!shouldLoad || !imageUrl) return

    const img = new Image()
    img.src = imageUrl
    img.onload = () => setLoaded(true)
  }, [shouldLoad, imageUrl])

  const defaultHeight = useBannerAndSliderHeights()
  const resolvedHeight = height ?? defaultHeight

  return (
    <Container ref={containerRef} $height={resolvedHeight}>
      {!loaded && imageUrl && (
        <BackgroundSkeleton variant='rectangular' animation='wave' />
      )}
      <Background
        $imageUrl={imageUrl}
        $blur={blur}
        $opacity={opacity}
        $loaded={loaded}
        aria-hidden
      />
      <Gradient />
      <Content>{children}</Content>
    </Container>
  )
}
